import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Eye, EyeOff, ArrowLeft, Mail, Lock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { validateEmail, validatePassword, sanitizeText, validateName } from "@/utils/validation";
import { useTranslation } from "react-i18next";
import { soundManager } from "@/utils/sounds";

import {AutoTranslate} from "@/lib/autoTranslate.tsx";
import { SEO } from "@/components/SEO";

export default function Auth() {
  const { t } = useTranslation();
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [passwordStrength, setPasswordStrength] = useState<string>("");

  const { signIn, signUp, signInWithGoogle } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'signup') {
      setIsSignUp(true);
    } else if (tab === 'signin') {
      setIsSignUp(false);
    }
  }, []);

const handleInputChange = (field: string, value: string) => {
  // Preserve spaces for fullName; still collapse multiple spaces to one
  const sanitizedValue =
    field === "fullName"
      ? value.replace(/\s+/g, " ") // allow spaces, collapse multiples
      : sanitizeText(value);

  setFormData(prev => ({ ...prev, [field]: sanitizedValue }));

  if (validationErrors[field]) {
    setValidationErrors(prev => ({ ...prev, [field]: "" }));
  }

  if (field === "password") {
    const { strength } = validatePassword(sanitizedValue);
    setPasswordStrength(strength || "");
  }
    // Check password match for confirmPassword field
    if (field === "confirmPassword" || field === "password") {
      const pwd = field === "password" ? sanitizedValue : formData.password;
      const confirmPwd = field === "confirmPassword" ? sanitizedValue : formData.confirmPassword;

      if (confirmPwd && pwd !== confirmPwd) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: "Passwords do not match" }));
      } else if (confirmPwd && pwd === confirmPwd) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: "" }));
      }
    }
  };

  const validateForm = (isSignUp: boolean = false) => {
    const errors: {[key: string]: string} = {};

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error!;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error!;
    }

    if (isSignUp) {
      if (!formData.confirmPassword) {
        errors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    }

     if (isSignUp) {
      const nameValidation = validateName(formData.fullName, "Full name");
      if (!nameValidation.isValid) {
        errors.fullName = nameValidation.error!;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm(false)) {
      return;
    }

    setLoading(true);
    setError(null);

    const { error } = await signIn(formData.email, formData.password);

    if (error) {
      setError(error.message);
    } else {
      soundManager.signIn();
      toast({
        title: "Welcome back!",
        description: "You've been signed in successfully.",
      });
      navigate('/');
    }

    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm(true)) {
      return;
    }

    setLoading(true);
    setError(null);

    const { error } = await signUp(formData.email, formData.password, formData.fullName);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      navigate('/verify-email', { state: { email: formData.email } });
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    const { error } = await signInWithGoogle();

    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col bg-gradient-main p-6">
      <SEO
        title="Sign In or Sign Up — FIND Resume Builder"
        description="Log in or create your free FIND Resume Builder account to start building AI-powered, ATS-friendly resumes and save your progress across devices."
        path="/auth"
      />
      <div
        className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
        style={{
          backgroundImage: `var(--grid-pattern)`,
        }}
      />

      <Link
        to="/landing"
        className="relative z-10 inline-flex items-center gap-2 px-4 py-2 text-foreground transition-colors w-fit group"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="relative inline-block">
          {t("backToHome")}
          <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-current transition-all duration-300 group-hover:w-full"></span>
        </span>
      </Link>

      <div className="relative z-10 w-full max-w-xl mx-auto flex-1 flex flex-col items-center justify-center">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-primary mb-2">F I N D</h1>
          <p className="text-lg text-muted-foreground">{AutoTranslate(`Resume Builder`)}</p>
        </div>

        <Card className="w-full bg-card/90 backdrop-blur-sm border-card-border overflow-hidden">
          <CardContent className="pt-6">
            <Tabs value={isSignUp ? "signup" : "signin"} onValueChange={(value) => setIsSignUp(value === "signup")} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin">{useTranslation().t('signIn')}</TabsTrigger>
                <TabsTrigger value="signup"> {useTranslation().t('signUp')}</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">{AutoTranslate(`Welcome Back`)}</h2>
                  <p className="text-sm text-muted-foreground">
                    {AutoTranslate(`Glad to see you're back! Sign in and continue from where you stopped.`)}
                  </p>
                </div>
                <form onSubmit={handleSignIn} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="signin-email">{AutoTranslate(`Email`)}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="example@gmail.com"
                        required
                        className={`pl-10 bg-input border-input-border ${validationErrors.email ? "border-destructive" : ""}`}
                      />
                    </div>
                    {validationErrors.email && (
                      <p className="text-sm text-destructive">{validationErrors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="signin-password">{AutoTranslate(`Password`)}</Label>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate('/forgot-password')}
                      >
                        {AutoTranslate(`Forgot your password?`)}
                      </Button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        placeholder="**********"
                        required
                        className={`pl-10 pr-10 bg-input border-input-border ${validationErrors.password ? "border-destructive" : ""}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {validationErrors.password && (
                      <p className="text-sm text-destructive">{validationErrors.password}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? AutoTranslate(`Signing In...`) : AutoTranslate(`Sign In`)}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">{AutoTranslate(`Or continue with`)}</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={loading}
                    onClick={handleGoogleSignIn}
                  >
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loading ? AutoTranslate(`Connecting...`) : AutoTranslate(`Continue with Google`)}
                  </Button>

                </form>

                <div className="mt-6 text-center">
                  <p className="text-xs text-muted-foreground">
                    {AutoTranslate(`By logging in, you agree to our`)}{' '}
                    <Link to="/terms" className="underline hover:no-underline">{AutoTranslate(`Terms`)}</Link>
                    {' '}{AutoTranslate(`and`)}{' '}
                    <Link to="/privacy" className="underline hover:no-underline">{AutoTranslate(`Privacy Policy`)}</Link>.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="signup">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">{AutoTranslate(`Create an Account`)}</h2>
                  <p className="text-sm text-muted-foreground">
                    {AutoTranslate(`Sign up now to create a new resume and impress recruiters.`)}
                  </p>
                </div>
                <form onSubmit={handleSignUp} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                   <div className="space-y-2">
                    <Label htmlFor="signup-name">{AutoTranslate(`Full Name`)}</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange("fullName", e.target.value)}
                      placeholder="John Doe"
                      required
                      className={`bg-input border-input-border ${validationErrors.fullName ? "border-destructive" : ""}`}
                    />
                    {validationErrors.fullName && (
                      <p className="text-sm text-destructive">{validationErrors.fullName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{AutoTranslate(`Email`)}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="example@gmail.com"
                        required
                        className={`pl-10 bg-input border-input-border ${validationErrors.email ? "border-destructive" : ""}`}
                      />
                    </div>
                    {validationErrors.email && (
                      <p className="text-sm text-destructive">{validationErrors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{AutoTranslate(`Password`)}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        placeholder="Minimum 8 characters"
                        required
                        className={`pl-10 pr-10 bg-input border-input-border ${validationErrors.password ? "border-destructive" : ""}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {validationErrors.password && (
                      <p className="text-sm text-destructive">{validationErrors.password}</p>
                    )}
                    {passwordStrength && !validationErrors.password && (
                      <p className="text-sm text-muted-foreground">
                        Password strength: {passwordStrength}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm-password">{AutoTranslate(`Confirm Password`)}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                        placeholder="Re-enter your password"
                        required
                        className={`pl-10 pr-10 bg-input border-input-border ${validationErrors.confirmPassword ? "border-destructive" : ""}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {validationErrors.confirmPassword && (
                      <p className="text-sm text-destructive">{validationErrors.confirmPassword}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || !!validationErrors.confirmPassword}
                  >
                    {loading ? AutoTranslate(`Creating Account...`) : AutoTranslate(`Sign Up`)}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={loading}
                    onClick={handleGoogleSignIn}
                  >
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loading ? AutoTranslate(`Connecting...`) : AutoTranslate(`Continue with Google`)}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-xs text-muted-foreground">
                    By signing up, you agree to our{' '}
                    <Link to="/terms" className="underline hover:no-underline">{AutoTranslate(`Terms`)}</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="underline hover:no-underline">{AutoTranslate(`Privacy Policy`)}</Link>.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}